import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '../../utils/api'
import { BaseWidget } from './BaseWidget'
import { BellIcon } from '../Icons'

interface Announcement {
  id: number
  title: string
  content: string
  type: 'feature' | 'improvement' | 'bug_fix' | 'maintenance'
  created_at: string
  created_by: string
}

interface WhatsNewWidgetProps {
  className?: string
  widgetId?: string
}

const CONTENT_PREVIEW_LENGTH = 150
const MINIMAL_CONTENT_LENGTH = 50

const WhatsNewWidget: React.FC<WhatsNewWidgetProps> = ({ className, widgetId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [latestAnnouncement, setLatestAnnouncement] = useState<Announcement | null>(null)
  const [loading, setLoading] = useState(true)
  const [isMinimal, setIsMinimal] = useState(false)

  // Detect widget height for minimal display
  useEffect(() => {
    const checkHeight = () => {
      const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'whats_new'}"]`)
      if (widgetElement) {
        const height = widgetElement.clientHeight
        setIsMinimal(height <= 230)
      }
    }
    
    checkHeight()
    const timer = setTimeout(checkHeight, 100)
    window.addEventListener('resize', checkHeight)
    const observer = new MutationObserver(checkHeight)
    const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'whats_new'}"]`)
    if (widgetElement) {
      observer.observe(widgetElement, { attributes: true, attributeFilter: ['style'] })
    }
    
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', checkHeight)
      observer.disconnect()
    }
  }, [widgetId])

  const fetchAnnouncements = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/announcements`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        // Handle both array and object responses
        const announcementsArray = Array.isArray(data) ? data : (data.announcements || [])
        // Get only the latest announcement
        if (announcementsArray.length > 0) {
          setLatestAnnouncement(announcementsArray[0])
        }
      }
    } catch (error) {
      console.error('Error fetching announcements:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAnnouncements()
  }, [])

  const handleViewAll = () => {
    navigate('/dashboard/announcements')
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'feature': return 'bg-blue-100 text-blue-900 dark:bg-blue-500/30 dark:text-blue-100'
      case 'improvement': return 'bg-green-100 text-green-900 dark:bg-green-500/30 dark:text-green-100'
      case 'bug_fix': return 'bg-red-100 text-red-900 dark:bg-red-500/30 dark:text-red-100'
      case 'maintenance': return 'bg-yellow-100 text-yellow-900 dark:bg-yellow-500/30 dark:text-yellow-100'
      default: return 'bg-gray-100 text-gray-900 dark:bg-gray-500/30 dark:text-gray-100'
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'feature': return t('dashboard.announcements.types.feature')
      case 'improvement': return t('dashboard.announcements.types.improvement')
      case 'bug_fix': return t('dashboard.announcements.types.bug_fix')
      case 'maintenance': return t('dashboard.announcements.types.maintenance')
      default: return type
    }
  }

  const isContentLong = latestAnnouncement && latestAnnouncement.content.length > CONTENT_PREVIEW_LENGTH
  const displayContent = latestAnnouncement
    ? isContentLong
      ? `${latestAnnouncement.content.substring(0, CONTENT_PREVIEW_LENGTH)}...`
      : latestAnnouncement.content
    : ''
  
  const minimalContent = latestAnnouncement
    ? latestAnnouncement.content.length > MINIMAL_CONTENT_LENGTH
      ? `${latestAnnouncement.content.substring(0, MINIMAL_CONTENT_LENGTH)}...`
      : latestAnnouncement.content
    : ''

  return (
    <BaseWidget
      title={t('dashboard.widgets.whatsNew')}
      icon={<BellIcon className="h-5 w-5" />}
      accent="announcements"
      variant="gradient"
      className={className}
      loading={loading}
      minimalDisplay={isMinimal}
    >
      {isMinimal ? (
        // Minimal display - readable announcement view with larger text
        <div className="flex items-center justify-center h-full px-3 py-2">
          <div className="text-center">
            {latestAnnouncement ? (
              <>
                <div className="text-xl font-bold text-white mb-2">{latestAnnouncement.title}</div>
                <div className="text-sm text-white/90 line-clamp-3 leading-relaxed">{minimalContent}</div>
                <span className={`text-xs px-3 py-1 rounded-full inline-block mt-2 font-medium ${getTypeColor(latestAnnouncement.type)}`}>
                  {getTypeLabel(latestAnnouncement.type)}
                </span>
              </>
            ) : (
              <div className="text-base text-white/90">No announcements</div>
            )}
          </div>
        </div>
      ) : (
        // Full display
        <div className="space-y-3 flex-1">
          {latestAnnouncement ? (
            <div className="space-y-3">
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-base text-white pr-2">{latestAnnouncement.title}</h4>
                  <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${getTypeColor(latestAnnouncement.type)}`}>
                    {getTypeLabel(latestAnnouncement.type)}
                  </span>
                </div>
                <p className="text-sm text-white/90 whitespace-pre-wrap">{displayContent}</p>
                {isContentLong && (
                  <button
                    onClick={handleViewAll}
                    className="mt-3 px-4 py-2 bg-white/15 hover:bg-white/25 text-white text-sm font-medium rounded-lg transition-all duration-200 inline-flex items-center gap-2"
                  >
                    {t('dashboard.announcements.continueReading')} →
                  </button>
                )}
                <p className="text-xs text-white/70 mt-3">
                  {new Date(latestAnnouncement.created_at).toLocaleDateString()} • {t('dashboard.widgets.by')} {latestAnnouncement.created_by}
                </p>
              </div>
              
              <button
                onClick={handleViewAll}
                className="w-full py-2.5 px-4 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg transition-all duration-200"
              >
                {t('dashboard.announcements.viewAllAnnouncements')}
              </button>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-white/90 text-sm">Welcome to MortgageFlow Pro!</p>
              <p className="text-white/70 text-xs mt-1">
                Hello! We're super excited to have you onboard. Keep an eye on that Announcement widget to keep track of the latest features.
              </p>
              <p className="text-white/70 text-xs mt-2">Thanks, Antoine</p>
            </div>
          )}
        </div>
      )}
    </BaseWidget>
  )
}

export default WhatsNewWidget